import React from 'react';
import {
  Checkbox as MuiCheckbox,
  FormControlLabel,
  FormControl,
  FormLabel,
  FormHelperText,
  FormGroup,
  Box,
  styled,
} from '@mui/material';
import type { CheckboxProps } from '@mui/material';
import { Check, IndeterminateCheckBox } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

// Option interface
export interface CheckboxOption {
  value: string | number;
  label: string;
  disabled?: boolean;
  description?: string;
  icon?: React.ReactNode;
}

// Custom checkbox variants
export type CheckboxVariant = 'default' | 'card' | 'switch';
export type CheckboxSize = 'small' | 'medium' | 'large';

// Extended props interface for group
export interface CustomCheckboxGroupProps {
  variant?: CheckboxVariant;
  size?: CheckboxSize;
  options: CheckboxOption[];
  value: (string | number)[];
  onChange: (value: (string | number)[]) => void;
  label?: string;
  description?: string;
  error?: boolean;
  helperText?: string;
  orientation?: 'horizontal' | 'vertical';
  max?: number; // Maximum selections allowed
}

// Individual checkbox props
export interface CustomCheckboxProps extends Omit<CheckboxProps, 'size'> {
  variant?: CheckboxVariant;
  size?: CheckboxSize;
  label?: string;
  description?: string;
  error?: boolean;
}

// Styled components
const StyledCheckbox = styled(MuiCheckbox)<{
  variant?: CheckboxVariant;
  size?: CheckboxSize;
  error?: boolean;
}>(({ theme, variant: checkboxVariant, size, error }) => {
  const sizeConfig = {
    small: {
      size: 16,
      fontSize: '0.75rem',
    },
    medium: {
      size: 20,
      fontSize: '0.875rem',
    },
    large: {
      size: 24,
      fontSize: '1rem',
    },
  };

  const currentSize = sizeConfig[size || 'medium'];
  const color = error ? theme.palette.error.main : theme.palette.primary.main;

  const baseStyles = {
    padding: theme.spacing(1),
    '&:hover': {
      backgroundColor: alpha(color, 0.04),
    },
    '&.Mui-checked': {
      color: color,
    },
    '& .MuiSvgIcon-root': {
      fontSize: currentSize.size,
    },
  };

  const variantStyles = {
    default: {},
    card: {
      borderRadius: theme.spacing(1),
      border: `2px solid ${theme.palette.grey[200]}`,
      padding: theme.spacing(1.5),
      margin: theme.spacing(0.5, 0),
      '&:hover': {
        borderColor: alpha(color, 0.5),
        boxShadow: `0 2px 8px ${alpha(color, 0.1)}`,
      },
      '&.Mui-checked': {
        borderColor: color,
        boxShadow: `0 2px 12px ${alpha(color, 0.2)}`,
      },
    },
    switch: {
      '& .MuiSvgIcon-root': {
        display: 'none',
      },
      width: 44,
      height: 24,
      padding: 0,
      borderRadius: 12,
      backgroundColor: theme.palette.grey[300],
      '&:hover': {
        backgroundColor: theme.palette.grey[400],
      },
      '&.Mui-checked': {
        backgroundColor: color,
        '&:hover': {
          backgroundColor: alpha(color, 0.8),
        },
      },
      '&:before': {
        content: '""',
        position: 'absolute',
        width: 16,
        height: 16,
        borderRadius: '50%',
        backgroundColor: 'white',
        left: 4,
        top: 4,
        transition: 'transform 0.2s ease-in-out',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
      },
      '&.Mui-checked:before': {
        transform: 'translateX(20px)',
      },
    },
  };

  return {
    ...baseStyles,
    ...variantStyles[checkboxVariant || 'default'],
  };
});

const CheckboxContainer = styled(Box)<{ orientation?: 'horizontal' | 'vertical' }>(
  ({ orientation }) => ({
    display: 'flex',
    flexDirection: orientation === 'horizontal' ? 'row' : 'column',
    gap: 8,
    flexWrap: orientation === 'horizontal' ? 'wrap' : 'nowrap',
  })
);

const OptionContent = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

const OptionDescription = styled('div')(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginTop: 2,
}));

export const Checkbox: React.FC<CustomCheckboxProps> = ({
  variant = 'default',
  size = 'medium',
  label,
  description,
  error = false,
  ...props
}) => {
  const checkboxElement = (
    <StyledCheckbox
      {...props}
      variant={variant}
      size={size === 'large' ? 'medium' : size} // MUI doesn't have large size
      error={error}
      icon={variant === 'switch' ? <div /> : undefined}
      checkedIcon={variant === 'switch' ? <div /> : <Check />}
      indeterminateIcon={<IndeterminateCheckBox />}
    />
  );

  if (!label && !description) {
    return checkboxElement;
  }

  return (
    <FormControlLabel
      control={checkboxElement}
      label={
        <OptionContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{label}</span>
          </Box>
          {description && <OptionDescription>{description}</OptionDescription>}
        </OptionContent>
      }
      sx={{
        margin: 0,
        alignItems: variant === 'card' ? 'flex-start' : 'center',
        '& .MuiFormControlLabel-label': {
          flex: 1,
        },
      }}
    />
  );
};

export const CheckboxGroup: React.FC<CustomCheckboxGroupProps> = ({
  variant = 'default',
  size = 'medium',
  options,
  value = [],
  onChange,
  label,
  description,
  error = false,
  helperText,
  orientation = 'vertical',
  max,
}) => {
  const displayHelperText = error ? helperText : description || helperText;

  const handleChange = (optionValue: string | number) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : max && value.length >= max
        ? value
        : [...value, optionValue];

    onChange(newValue);
  };

  return (
    <FormControl error={error} component="fieldset">
      {label && (
        <FormLabel component="legend" sx={{ mb: 1, fontSize: '0.875rem', fontWeight: 500 }}>
          {label}
        </FormLabel>
      )}

      <FormGroup>
        <CheckboxContainer orientation={orientation}>
          {options.map(option => (
            <FormControlLabel
              key={option.value}
              disabled={
                option.disabled || !!(max && value.length >= max && !value.includes(option.value))
              }
              control={
                <StyledCheckbox
                  variant={variant}
                  size={size === 'large' ? 'medium' : size}
                  error={error}
                  checked={value.includes(option.value)}
                  onChange={() => handleChange(option.value)}
                  icon={variant === 'switch' ? <div /> : undefined}
                  checkedIcon={variant === 'switch' ? <div /> : <Check />}
                />
              }
              label={
                <OptionContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {option.icon && option.icon}
                    <span>{option.label}</span>
                  </Box>
                  {option.description && (
                    <OptionDescription>{option.description}</OptionDescription>
                  )}
                </OptionContent>
              }
              sx={{
                margin: 0,
                alignItems: variant === 'card' ? 'flex-start' : 'center',
                '& .MuiFormControlLabel-label': {
                  flex: 1,
                },
              }}
            />
          ))}
        </CheckboxContainer>
      </FormGroup>

      {displayHelperText && <FormHelperText sx={{ mt: 1 }}>{displayHelperText}</FormHelperText>}

      {max && (
        <FormHelperText sx={{ mt: 0.5, fontSize: '0.7rem' }}>
          {value.length}/{max} selected
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default CheckboxGroup;
